import React, { useEffect, useState } from "react"

import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
} from "@mui/material"

import { KcTable } from "../../components/common/kc-table"
import { useAxiosPrivate, useDocumentTitle } from "../../hooks"
import { PAGE_TITLE, VALIDATION_RULES } from "../../constants/constants"
import Layout from "../../layouts/common/Layout"
import Loader from "../../components/common/Loader"
import toast from "react-hot-toast"
import { METHODS, errorHandler } from "../../hooks/useFetch"

import DeleteIcon from "@mui/icons-material/Delete"
import AddIcon from "@mui/icons-material/Add"

const WhitelistManagement = () => {
  useDocumentTitle(PAGE_TITLE.WHITELIST)

  const axiosPrivate = useAxiosPrivate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [whitelist, setWhitelist] = useState([])

  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [newTaskId, setNewTaskId] = useState("")
  const [newEmail, setNewEmail] = useState("")
  const [addLoading, setAddLoading] = useState(false)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [entryToDelete, setEntryToDelete] = useState(null)
  const [confirmText, setConfirmText] = useState("")

  const fetchWhitelist = async () => {
    setLoading(true)
    try {
      const response = await axiosPrivate[METHODS.GET.toLowerCase()](
        "whitelist"
      )
      setWhitelist(response.data.whitelist || [])
      setError(null)
    } catch (err) {
      setError(errorHandler(err))
      toast.error("Failed to fetch whitelist")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchWhitelist()
    window.sidebar.open()
  }, [])

  const handleAdd = async () => {
    if (!newTaskId || !newEmail) {
      toast.error("Task ID and Email are required")
      return
    }

    const emailValidation = VALIDATION_RULES.EMAIL(newEmail)
    if (emailValidation !== true) {
      toast.error(emailValidation)
      return
    }

    setAddLoading(true)
    try {
      await axiosPrivate[METHODS.POST.toLowerCase()](`whitelist`, {
        task_id: parseInt(newTaskId),
        email: newEmail,
      })
      toast.success("Whitelist entry added successfully")
      setAddDialogOpen(false)
      setNewTaskId("")
      setNewEmail("")
      await fetchWhitelist()
    } catch (err) {
      toast.error(errorHandler(err).message || "Failed to add whitelist entry")
    } finally {
      setAddLoading(false)
    }
  }

  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry)
    setDeleteDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setEntryToDelete(null)
    setConfirmText("")
  }

  const confirmDelete = async () => {
    if (!entryToDelete) return

    setLoading(true)
    try {
      await axiosPrivate[METHODS.DELETE.toLowerCase()](
        `whitelist/${entryToDelete.id}`
      )
      toast.success("Whitelist entry deleted successfully")
      handleCloseDeleteDialog()
      await fetchWhitelist()
    } catch (err) {
      toast.error(
        errorHandler(err).message || "Failed to delete whitelist entry"
      )
    } finally {
      setLoading(false)
    }
  }

  const renderTaskIdCell = (params) => {
    const { value } = params
    return value ?? "null"
  }

  const columns = [
    {
      field: "task_id",
      headerName: "Task ID",
      minWidth: 120,
      flex: 1,
      renderCell: renderTaskIdCell,
    },
    { field: "email", headerName: "Email", minWidth: 250, flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      minWidth: 80,
      flex: 1,
      getActions: (params) => {
        return [
          <IconButton
            key={`${params.row.id}-DELETE`}
            onClick={() => handleDeleteClick(params.row)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>,
        ]
      },
    },
  ]

  return (
    <Layout.CenterWrapper>
      <Box sx={{ width: "100%", maxWidth: 1000, p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Entry
          </Button>
        </Box>

        {loading ? (
          <Loader />
        ) : error ? (
          <Typography color="error">
             Error: {error?.message || `Request failed (status ${error?.status ?? "unknown"})`}
          </Typography>
        ) : (
          <Box sx={{ height: 500 }}>
            <KcTable
              rows={whitelist}
              columns={columns}
              disableSelectionOnClick
              initialState={{
                sorting: {
                  sortModel: [
                    { field: "task_id", sort: "desc" },
                    { field: "email", sort: "asc" },
                  ],
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Add Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Whitelist Entry</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Task ID"
            value={newTaskId}
            onChange={(e) => setNewTaskId(e.target.value)}
            type="number"
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            type="email"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            variant="contained"
            disabled={addLoading || !newTaskId || !newEmail}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Whitelist Entry</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            ⚠️ Warning: This will remove access for{" "}
            <strong>{entryToDelete?.email}</strong> to Task{" "}
            <strong>{entryToDelete?.task_id}</strong>.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please enter <strong>'delete forever'</strong> below to confirm
            deletion.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Confirm deletion"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="delete forever"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={confirmText !== "delete forever"}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Layout.CenterWrapper>
  )
}

export default WhitelistManagement